import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles, ShieldCheck, Trash2,
  CheckCircle, AlertCircle, KeyRound, User as UserIcon, Loader2, Phone,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Separator } from './ui/separator';
import { AuthContext } from '../contexts/AppContexts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===========================================================
// Helpers
// ===========================================================
export function extractApiError(err, fallback = "Une erreur est survenue.") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  }
  if (detail?.msg) return detail.msg;
  return fallback;
}

function passwordStrength(pwd) {
  if (!pwd) return { score: 0, label: '', color: 'bg-white/10' };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  const levels = [
    { label: 'Très faible', color: 'bg-red-500' },
    { label: 'Faible', color: 'bg-orange-500' },
    { label: 'Moyen', color: 'bg-yellow-500' },
    { label: 'Bon', color: 'bg-lime-500' },
    { label: 'Excellent', color: 'bg-green-500' },
  ];
  return { score, ...levels[Math.min(score, levels.length - 1)] };
}

// ===========================================================
// Auth Choice Modal (Google Auth UI désactivé — backend conservé)
// La modal redirige désormais directement vers la page Connexion email/MDP.
// ===========================================================
export function AuthChoiceModal({ open, onOpenChange, googleLogin: _googleLogin }) {
  const navigate = useNavigate();
  const ctx = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Reset les champs quand la modal se ferme (sécurité + UX)
  useEffect(() => {
    if (!open) {
      setEmail('');
      setPassword('');
      setLoading(false);
      setShowPassword(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, remember: true }, { withCredentials: true });
      if (ctx?.setUser) ctx.setUser(data.user);
      onOpenChange(false);
      toast.success("Connexion réussie ✨");
    } catch (err) {
      toast.error(extractApiError(err, "Email ou mot de passe incorrect."));
    } finally {
      setLoading(false);
    }
  };

  const goToRegister = () => {
    // On mémorise la page courante pour y revenir après inscription + vérif email.
    try { sessionStorage.setItem('post_login_redirect', window.location.pathname + window.location.search); } catch (_e) { /* sessionStorage indispo */ }
    onOpenChange(false);
    navigate('/inscription');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#261B3B] border-white/10 max-w-md" data-testid="auth-choice-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">Connexion</DialogTitle>
          <DialogDescription className="text-sm text-[#B59ED1]">
            Connecte-toi pour continuer. Tu restes sur la page où tu étais.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="auth-modal-login-form">
          <div className="space-y-1.5">
            <Label htmlFor="amodal-email" className="text-[#E2D5F8] text-sm">Email</Label>
            <Input
              id="amodal-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              className="bg-[#1B132B] border-white/10 h-11 text-white"
              data-testid="auth-modal-email"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="amodal-password" className="text-[#E2D5F8] text-sm">Mot de passe</Label>
              <button
                type="button"
                onClick={() => { onOpenChange(false); navigate('/mot-de-passe-oublie'); }}
                className="text-xs text-[#00E5FF] hover:underline"
                data-testid="auth-modal-forgot"
              >
                Oublié ?
              </button>
            </div>
            <div className="relative">
              <Input
                id="amodal-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-[#1B132B] border-white/10 h-11 text-white pr-12"
                data-testid="auth-modal-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B59ED1] hover:text-white text-xs"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? 'Masquer' : 'Voir'}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-[#D926A9] to-[#00E5FF] hover:opacity-90 text-white"
            data-testid="auth-modal-submit"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </Button>
        </form>

        <div className="pt-3 mt-3 border-t border-white/10">
          <p className="text-xs text-center text-[#B59ED1]">
            Pas encore de compte ?{' '}
            <button
              type="button"
              onClick={goToRegister}
              className="text-[#00E5FF] hover:underline font-medium"
              data-testid="auth-go-register"
            >
              Crée-le ici
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================
// Auth Form Layout
// ===========================================================
function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <main className="min-h-screen bg-[#1B132B] pt-24 pb-16 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Sparkles className="w-8 h-8 text-[#D926A9] mx-auto mb-3" />
          <h1 className="font-serif text-3xl text-white mb-2">{title}</h1>
          {subtitle && <p className="text-[#B59ED1] text-sm">{subtitle}</p>}
        </div>
        <Card className="bg-[#261B3B] border-white/10 shadow-xl">
          <CardContent className="p-6">{children}</CardContent>
        </Card>
        {footer && <div className="text-center mt-6 text-sm text-[#B59ED1]">{footer}</div>}
      </motion.div>
    </main>
  );
}

// ===========================================================
// PasswordInput component
// ===========================================================
function PasswordInput({ value, onChange, autoComplete = 'current-password', placeholder = '••••••••', name = 'password', testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        name={name}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#1B132B] border-white/10 pl-10 pr-10 h-11 text-white"
        data-testid={testid}
      />
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B59ED1]" />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B59ED1] hover:text-white"
        tabIndex={-1}
        aria-label={show ? 'Masquer' : 'Afficher'}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ===========================================================
// LoginPage — /connexion
// ===========================================================
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, googleLogin } = useAuthFromContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  // ?next=/checkout permet de revenir à la page d'origine après login
  useEffect(() => {
    const next = searchParams.get('next');
    if (next && !next.startsWith('http')) {
      try { sessionStorage.setItem('post_login_redirect', next); } catch (_e) {}
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, remember }, { withCredentials: true });
      setUser(data.user);
      toast.success("Connexion réussie");
      const redirect = sessionStorage.getItem('post_login_redirect') || '/dashboard';
      sessionStorage.removeItem('post_login_redirect');
      navigate(redirect);
    } catch (err) {
      toast.error(extractApiError(err, "Email ou mot de passe incorrect."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Bon retour"
      subtitle={searchParams.get('next') === '/checkout'
        ? "Connecte-toi pour finaliser ta commande"
        : "Connecte-toi à ton espace personnel"}
      footer={(
        <>Pas encore de compte ? <Link to={`/inscription${searchParams.get('next') ? `?next=${encodeURIComponent(searchParams.get('next'))}` : ''}`} className="text-[#00E5FF] hover:underline" data-testid="login-to-register">Crée-le ici</Link></>
      )}
    >
      {searchParams.get('next') === '/checkout' && (
        <div className="mb-4 p-3 rounded-lg border border-[#00E5FF]/25 bg-[#00E5FF]/5 text-xs text-[#E2D5F8]">
          <p className="font-medium text-[#00E5FF] mb-1">Pourquoi un compte ?</p>
          <p>Pour suivre ta commande, recevoir ta facture et retrouver tes adresses lors de tes prochains achats — en toute sécurité.</p>
        </div>
      )}

      {/* Google Auth UI masqué — backend conservé intact. */}
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#E2D5F8]">Email</Label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              className="bg-[#1B132B] border-white/10 pl-10 h-11 text-white"
              data-testid="login-email"
            />
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B59ED1]" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-[#E2D5F8]">Mot de passe</Label>
            <Link to="/mot-de-passe-oublie" className="text-xs text-[#00E5FF] hover:underline" data-testid="login-forgot">
              Oublié ?
            </Link>
          </div>
          <PasswordInput value={password} onChange={setPassword} testid="login-password" />
        </div>

        <label className="flex items-center gap-2 text-sm text-[#B59ED1] cursor-pointer">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="rounded border-white/20"
            data-testid="login-remember"
          />
          Se souvenir de moi
        </label>

        <Button type="submit" disabled={loading} className="w-full gradient-primary h-11" data-testid="login-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          Se connecter
        </Button>
      </form>
    </AuthLayout>
  );
}

// ===========================================================
// RegisterPage — /inscription
// ===========================================================
export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { googleLogin, setUser } = useAuthFromContext();
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', phone: '', accept_rgpd: false });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [wixActivation, setWixActivation] = useState(false);
  const [nlChoice, setNlChoice] = useState(null); // null | 'yes' | 'no'
  const [nlLoading, setNlLoading] = useState(false);

  const handleNewsletterChoice = async (yes) => {
    if (!yes) {
      setNlChoice('no');
      return;
    }
    setNlLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
      });
      setNlChoice('yes');
      toast.success("Merci ! Tu fais désormais partie de la newsletter de Bilan 2 Vie 🌙");
    } catch (err) {
      toast.error(extractApiError(err, "Inscription newsletter impossible pour le moment."));
    } finally {
      setNlLoading(false);
    }
  };
  const strength = passwordStrength(form.password);

  // ?next=/checkout permet de revenir à la page d'origine après vérif email + login
  useEffect(() => {
    const next = searchParams.get('next');
    if (next && !next.startsWith('http')) {
      try { sessionStorage.setItem('post_login_redirect', next); } catch (_e) {}
    }
  }, [searchParams]);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_rgpd) {
      toast.error("Tu dois accepter la politique de confidentialité.");
      return;
    }
    if (strength.score < 2) {
      toast.error("Choisis un mot de passe plus fort (min. 8 caractères, lettres + chiffres).");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, form, { withCredentials: true });
      const isWixActivation = res.data?.wix_account_activation === true;
      // Connexion immédiate (plus de validation d'email bloquante) : on reprend direct.
      if (res.data?.user) {
        setUser(res.data.user);
        toast.success("Compte créé ! On continue 🌙");
        let redirect = '/dashboard';
        try { redirect = sessionStorage.getItem('post_login_redirect') || '/dashboard'; sessionStorage.removeItem('post_login_redirect'); } catch (_e) {}
        navigate(redirect);
        return;
      }
      setSubmitted(true);
      setWixActivation(isWixActivation);
      toast.success(isWixActivation
        ? "Compte existant détecté — lien d'activation envoyé par email !"
        : "Vérifie ta boîte mail pour activer ton compte.");
    } catch (err) {
      toast.error(extractApiError(err, "Erreur lors de l'inscription."));
    } finally {
      setLoading(false);
    }
  };

  // Détecte la page d'origine pour proposer un bouton de retour cohérent
  const _resumeUrl = (() => {
    try { return sessionStorage.getItem('post_login_redirect') || ''; } catch (_e) { return ''; }
  })();
  const _resumeLabel = (() => {
    if (!_resumeUrl || _resumeUrl === '/' || _resumeUrl === '/dashboard') return null;
    if (_resumeUrl.startsWith('/booking')) return 'Reprendre ma réservation';
    if (_resumeUrl.startsWith('/evenements')) return "Reprendre l'inscription à l'événement";
    if (_resumeUrl.startsWith('/checkout') || _resumeUrl.startsWith('/panier')) return 'Reprendre ma commande';
    if (_resumeUrl.startsWith('/services')) return 'Retour aux services';
    if (_resumeUrl.startsWith('/boutique')) return 'Retour à la boutique';
    return 'Reprendre où j\'en étais';
  })();

  if (submitted) {
    return (
      <AuthLayout
        title={wixActivation ? "Compte existant détecté ✨" : "Vérifie ta boîte mail"}
        subtitle={wixActivation ? "On t'a envoyé un lien pour définir ton mot de passe" : "On t'a envoyé un lien de confirmation"}
      >
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-[#00E5FF]/20 flex items-center justify-center mx-auto">
            <Mail className="w-8 h-8 text-[#00E5FF]" />
          </div>
          {wixActivation ? (
            <>
              <p className="text-[#E2D5F8] text-sm">
                Ton email <strong className="text-white">{form.email}</strong> figurait déjà dans notre ancien fichier client.
                <br />Un email vient de te parvenir pour définir ton mot de passe et activer ton compte.
              </p>
              <p className="text-xs text-[#B59ED1]">
                Pas reçu ? Pense à vérifier tes spams ou{' '}
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/auth/forgot-password`, { email: form.email });
                      toast.success("Lien renvoyé !");
                    } catch (e) { toast.error("Erreur"); }
                  }}
                  className="text-[#00E5FF] hover:underline"
                  data-testid="register-wix-resend"
                >clique ici pour le renvoyer</button>.
              </p>
            </>
          ) : (
            <>
              <p className="text-[#E2D5F8] text-sm">
                Un email a été envoyé à <strong className="text-white">{form.email}</strong>.
                <br />Clique sur le lien pour activer ton compte.
              </p>
              <p className="text-xs text-[#B59ED1]">
                Pas reçu après quelques minutes ? Vérifie tes spams ou{' '}
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/auth/resend-verification`, { email: form.email });
                      toast.success("Email renvoyé !");
                    } catch (e) { toast.error("Erreur"); }
                  }}
                  className="text-[#00E5FF] hover:underline"
                  data-testid="register-resend"
                >renvoie le lien</button>.
              </p>
            </>
          )}
          <Button onClick={() => navigate(_resumeUrl || '/')} className="w-full gradient-primary" data-testid="register-resume-cta">
            {_resumeLabel || "Retour à l'accueil"}
          </Button>
          {_resumeLabel && (
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-xs text-[#B59ED1] hover:text-white underline mt-2"
              data-testid="register-home-link"
            >
              ou retourner à l'accueil
            </button>
          )}

          {/* Modale newsletter — proposée après inscription, à part du form principal (RGPD) */}
          {nlChoice === null && (
            <div className="mt-6 p-5 rounded-2xl border border-[#D926A9]/30 bg-gradient-to-br from-[#3A115E]/40 via-[#261B3B]/60 to-[#1B132B]/40 text-left" data-testid="newsletter-modal">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[#D926A9]" />
                <h3 className="font-serif text-lg text-white">Rejoins la lumière du cercle</h3>
              </div>
              <p className="text-sm text-[#E2D5F8] leading-relaxed mb-4">
                Reçois en avant-première les dates de mes <strong className="text-white">ateliers</strong>, <strong className="text-white">soirées spirituelles</strong> et <strong className="text-white">événements collectifs</strong>, ainsi que des guidances inspirantes chaque mois. Pas de spam, juste de la magie.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleNewsletterChoice(true)}
                  disabled={nlLoading}
                  className="gradient-primary flex-1"
                  data-testid="newsletter-yes"
                >
                  {nlLoading ? "..." : "✨ Je m'inscris"}
                </Button>
                <Button
                  onClick={() => handleNewsletterChoice(false)}
                  variant="ghost"
                  className="text-[#B59ED1] hover:text-white"
                  data-testid="newsletter-no"
                >
                  Plus tard
                </Button>
              </div>
            </div>
          )}
          {nlChoice === 'yes' && (
            <div className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-sm text-green-300" data-testid="newsletter-confirmation">
              ✨ Inscription newsletter confirmée. Un mot de bienvenue arrive dans ta boîte.
            </div>
          )}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Bienvenue"
      subtitle="Crée ton compte en quelques secondes"
      footer={(<>Déjà un compte ? <Link to={`/connexion${searchParams.get('next') ? `?next=${encodeURIComponent(searchParams.get('next'))}` : ''}`} className="text-[#00E5FF] hover:underline" data-testid="register-to-login">Connecte-toi</Link></>)}
    >
      {/* Google Auth UI masqué — backend conservé intact. */}

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-[#E2D5F8]">Prénom</Label>
            <div className="relative">
              <Input
                required
                value={form.first_name}
                onChange={(e) => update('first_name', e.target.value)}
                placeholder="Prénom"
                className="bg-[#1B132B] border-white/10 pl-10 h-11 text-white"
                data-testid="register-first-name"
                autoComplete="given-name"
                minLength={1}
                maxLength={60}
              />
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B59ED1]" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#E2D5F8]">Nom</Label>
            <Input
              required
              value={form.last_name}
              onChange={(e) => update('last_name', e.target.value)}
              placeholder="Nom"
              className="bg-[#1B132B] border-white/10 h-11 text-white"
              data-testid="register-last-name"
              autoComplete="family-name"
              minLength={1}
              maxLength={60}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#E2D5F8]">Email</Label>
          <div className="relative">
            <Input
              type="email"
              required
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="ton@email.com"
              className="bg-[#1B132B] border-white/10 pl-10 h-11 text-white"
              data-testid="register-email"
              autoComplete="email"
            />
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B59ED1]" />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#E2D5F8]">Téléphone</Label>
          <div className="relative">
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              placeholder="06 12 34 56 78 ou +33 6 12 34 56 78"
              className="bg-[#1B132B] border-white/10 pl-10 h-11 text-white"
              data-testid="register-phone"
              maxLength={20}
              minLength={6}
            />
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B59ED1]" />
          </div>
          <p className="text-[10px] text-[#B59ED1]">Format français ou international (+33). Nécessaire pour confirmer tes rendez-vous et inscriptions.</p>
        </div>

        <div className="space-y-2">
          <Label className="text-[#E2D5F8]">Mot de passe</Label>
          <PasswordInput
            value={form.password}
            onChange={(v) => update('password', v)}
            autoComplete="new-password"
            placeholder="8 caractères min."
            name="new-password"
            testid="register-password"
          />
          {form.password && (
            <div className="space-y-1" data-testid="password-strength">
              <div className="flex gap-1 h-1">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className={`flex-1 rounded ${i < strength.score ? strength.color : 'bg-white/10'}`}></div>
                ))}
              </div>
              <p className="text-xs text-[#B59ED1]">Force : <span className="text-white">{strength.label}</span></p>
            </div>
          )}
        </div>

        <label className="flex items-start gap-2 text-xs text-[#B59ED1] cursor-pointer">
          <input
            type="checkbox"
            checked={form.accept_rgpd}
            onChange={(e) => update('accept_rgpd', e.target.checked)}
            className="rounded border-white/20 mt-0.5"
            data-testid="register-rgpd"
            required
          />
          <span>
            J'accepte la{' '}
            <Link to="/politique-confidentialite" target="_blank" className="text-[#00E5FF] hover:underline">
              politique de confidentialité
            </Link>{' '}
            et les{' '}
            <Link to="/cgv" target="_blank" className="text-[#00E5FF] hover:underline">CGV</Link>.
          </span>
        </label>

        <Button type="submit" disabled={loading} className="w-full gradient-primary h-11" data-testid="register-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Créer mon compte
        </Button>
      </form>
    </AuthLayout>
  );
}

// ===========================================================
// VerifyEmailPage — /verifier-email/:token
// ===========================================================
export function VerifyEmailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | error

  useEffect(() => {
    const run = async () => {
      try {
        await axios.get(`${API}/auth/verify-email/${token}`);
        setStatus('success');
      } catch (e) {
        setStatus('error');
      }
    };
    run();
  }, [token]);

  return (
    <AuthLayout
      title={status === 'success' ? "Email confirmé !" : status === 'error' ? "Lien invalide" : "Vérification…"}
      subtitle={status === 'success' ? "Tu peux maintenant te connecter à ton compte." : ""}
    >
      <div className="text-center space-y-5">
        {status === 'loading' && <Loader2 className="w-12 h-12 animate-spin text-[#D926A9] mx-auto" />}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            {(() => {
              // Si l'utilisateur revenait d'un tunnel (booking/évent/checkout), on lui
              // propose de reprendre directement à cet endroit après s'être connecté.
              let resumeUrl = '';
              try { resumeUrl = sessionStorage.getItem('post_login_redirect') || ''; } catch (_e) { /* */ }
              const nextParam = resumeUrl && resumeUrl !== '/' ? `?next=${encodeURIComponent(resumeUrl)}` : '';
              const label = !resumeUrl || resumeUrl === '/' || resumeUrl === '/dashboard'
                ? 'Aller à la connexion'
                : resumeUrl.startsWith('/booking') ? 'Se connecter et reprendre la réservation'
                : resumeUrl.startsWith('/evenements') ? "Se connecter et reprendre l'inscription"
                : resumeUrl.startsWith('/checkout') || resumeUrl.startsWith('/panier') ? 'Se connecter et reprendre la commande'
                : 'Se connecter et reprendre';
              return (
                <Button
                  onClick={() => navigate('/connexion' + nextParam)}
                  className="w-full gradient-primary"
                  data-testid="verify-go-login"
                >
                  {label}
                </Button>
              );
            })()}
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-sm text-[#B59ED1]">Ce lien est invalide ou a expiré. Demande un nouvel email de confirmation depuis la page de connexion.</p>
            <Button onClick={() => navigate('/connexion')} variant="outline" className="w-full border-white/10 text-[#E2D5F8]">
              Retour à la connexion
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

// ===========================================================
// ForgotPasswordPage — /mot-de-passe-oublie
// ===========================================================
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Toujours afficher OK pour ne pas leaker
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title="Vérifie ta boîte mail" subtitle="Si un compte existe avec cet email, un lien t'a été envoyé.">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-[#00E5FF]/20 flex items-center justify-center mx-auto">
            <Mail className="w-8 h-8 text-[#00E5FF]" />
          </div>
          <p className="text-xs text-[#B59ED1]">Le lien est valable 1 heure. Pense à vérifier tes spams.</p>
          <Link to="/connexion" className="text-sm text-[#00E5FF] hover:underline inline-block" data-testid="forgot-back-login">
            Retour à la connexion
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Mot de passe oublié"
      subtitle="Entre ton email pour recevoir un lien de réinitialisation"
      footer={(<Link to="/connexion" className="text-[#00E5FF] hover:underline">Retour à la connexion</Link>)}
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
        <div className="space-y-2">
          <Label className="text-[#E2D5F8]">Email</Label>
          <div className="relative">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              className="bg-[#1B132B] border-white/10 pl-10 h-11 text-white"
              data-testid="forgot-email"
              autoComplete="email"
            />
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B59ED1]" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full gradient-primary h-11" data-testid="forgot-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
          Envoyer le lien
        </Button>
      </form>
    </AuthLayout>
  );
}

// ===========================================================
// ResetPasswordPage — /reinitialiser-mot-de-passe/:token
// ===========================================================
export function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const strength = passwordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    if (strength.score < 2) {
      toast.error("Mot de passe trop faible.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success("Mot de passe mis à jour ! Connecte-toi.");
      navigate('/connexion');
    } catch (err) {
      toast.error(extractApiError(err, "Lien invalide ou expiré."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Nouveau mot de passe" subtitle="Choisis un mot de passe solide">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-2">
          <Label className="text-[#E2D5F8]">Nouveau mot de passe</Label>
          <PasswordInput value={password} onChange={setPassword} autoComplete="new-password" testid="reset-password" />
          {password && (
            <div className="flex gap-1 h-1">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className={`flex-1 rounded ${i < strength.score ? strength.color : 'bg-white/10'}`}></div>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-[#E2D5F8]">Confirmer</Label>
          <PasswordInput value={confirm} onChange={setConfirm} autoComplete="new-password" testid="reset-confirm" />
        </div>
        <Button type="submit" disabled={loading} className="w-full gradient-primary h-11" data-testid="reset-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
          Mettre à jour
        </Button>
      </form>
    </AuthLayout>
  );
}

// ===========================================================
// ConfirmEmailChangePage — /confirmer-email/:token
// ===========================================================
export function ConfirmEmailChangePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/confirm-email-change/${token}`);
        setNewEmail(data.new_email || '');
        setStatus('success');
      } catch (e) {
        setStatus('error');
      }
    };
    run();
  }, [token]);

  return (
    <AuthLayout
      title={status === 'success' ? "Email mis à jour" : status === 'error' ? "Lien invalide" : "Confirmation…"}
    >
      <div className="text-center space-y-5">
        {status === 'loading' && <Loader2 className="w-12 h-12 animate-spin text-[#D926A9] mx-auto" />}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-sm text-[#E2D5F8]">
              Ton compte est désormais associé à <strong className="text-white">{newEmail}</strong>.
            </p>
            <Button onClick={() => navigate('/connexion')} className="w-full gradient-primary" data-testid="confirm-email-go-login">
              Aller à la connexion
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-sm text-[#B59ED1]">Ce lien n'est plus valide. Refais une demande depuis ton profil.</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full border-white/10 text-[#E2D5F8]">
              Retour au dashboard
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

// ===========================================================
// ProfileSecuritySection (dans le dashboard)
// ===========================================================
let _useAuthRef = null;
export function _setUseAuth(fn) { _useAuthRef = fn; }
function useAuthFromContext() {
  if (!_useAuthRef) throw new Error("useAuth not registered");
  return _useAuthRef();
}

export function ProfileSecuritySection() {
  const { user, setUser, logout } = useAuthFromContext();
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || (user?.name ? user.name.split(' ')[0] : ''),
    last_name: user?.last_name || (user?.name ? user.name.split(' ').slice(1).join(' ') : ''),
    phone: user?.phone || '',
    newsletter_opt_in: !!user?.newsletter_opt_in,
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Change password
  const [showPwd, setShowPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ old: '', new: '' });
  const [pwdLoading, setPwdLoading] = useState(false);

  // Change email
  const [showEmail, setShowEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [emailLoading, setEmailLoading] = useState(false);

  // Delete account
  const [showDelete, setShowDelete] = useState(false);
  const [delForm, setDelForm] = useState({ password: '', confirmation: '' });
  const [delLoading, setDelLoading] = useState(false);

  // Set initial password (compte Google → migration vers email)
  const [showSetInitial, setShowSetInitial] = useState(false);
  const [initialPwdForm, setInitialPwdForm] = useState({ new: '' });
  const [initialPwdLoading, setInitialPwdLoading] = useState(false);

  const isEmail = user?.auth_method === 'email';

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, profileForm, { withCredentials: true });
      setUser(data.user);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { old_password: pwdForm.old, new_password: pwdForm.new }, { withCredentials: true });
      toast.success("Mot de passe modifié");
      setShowPwd(false);
      setPwdForm({ old: '', new: '' });
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setPwdLoading(false);
    }
  };

  const setInitialPassword = async (e) => {
    e.preventDefault();
    setInitialPwdLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/set-initial-password`, { new_password: initialPwdForm.new }, { withCredentials: true });
      toast.success(data?.message || "Mot de passe défini avec succès");
      setShowSetInitial(false);
      setInitialPwdForm({ new: '' });
      // Mettre à jour le user en contexte (auth_method passe à 'email')
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (me.data?.user) setUser(me.data.user);
      } catch (_e) { /* le user re-fetch au prochain mount */ }
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setInitialPwdLoading(false);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    try {
      await axios.post(`${API}/auth/change-email`, emailForm, { withCredentials: true });
      toast.success("Email envoyé sur la nouvelle adresse pour confirmation");
      setShowEmail(false);
      setEmailForm({ new_email: '', password: '' });
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setEmailLoading(false);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (delForm.confirmation.trim().toUpperCase() !== 'SUPPRIMER') {
      toast.error('Tape "SUPPRIMER" en majuscules pour confirmer.');
      return;
    }
    setDelLoading(true);
    try {
      await axios.delete(`${API}/auth/account`, { data: delForm, withCredentials: true });
      toast.success("Compte désactivé");
      setUser(null);
      window.location.href = '/';
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setDelLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-security-section">
      {/* Infos perso */}
      <Card className="bg-[#261B3B] border-white/10">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-white flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-[#D926A9]" />
            Mes informations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#E2D5F8]">Prénom</Label>
                <Input
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  className="bg-[#1B132B] border-white/10 mt-1 text-white"
                  data-testid="profile-first-name"
                  required
                  maxLength={60}
                />
              </div>
              <div>
                <Label className="text-[#E2D5F8]">Nom</Label>
                <Input
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  className="bg-[#1B132B] border-white/10 mt-1 text-white"
                  data-testid="profile-last-name"
                  required
                  maxLength={60}
                />
              </div>
            </div>
            <div>
              <Label className="text-[#E2D5F8]">Téléphone (optionnel)</Label>
              <Input
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                placeholder="+33 6 12 34 56 78"
                className="bg-[#1B132B] border-white/10 mt-1 text-white"
                data-testid="profile-phone"
              />
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <div>
                <p className="text-sm text-white">Newsletter</p>
                <p className="text-xs text-[#B59ED1]">Reçois nos nouveautés et offres (max 1/mois)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={profileForm.newsletter_opt_in}
                  onChange={(e) => setProfileForm({ ...profileForm, newsletter_opt_in: e.target.checked })}
                  className="sr-only peer"
                  data-testid="profile-newsletter"
                />
                <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:bg-[#D926A9] transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
              </label>
            </div>
            <Button type="submit" disabled={savingProfile} className="gradient-primary" data-testid="profile-save">
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Sécurité — uniquement pour les comptes email/MDP */}
      {isEmail && (
        <Card className="bg-[#261B3B] border-white/10">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#00E5FF]" />
              Sécurité
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#1B132B] rounded">
              <div>
                <p className="text-sm text-white">Email</p>
                <p className="text-xs text-[#B59ED1]">{user.email}</p>
              </div>
              <Button size="sm" variant="outline" className="border-white/10 text-[#00E5FF]" onClick={() => setShowEmail(!showEmail)} data-testid="security-change-email-toggle">
                Modifier
              </Button>
            </div>
            {showEmail && (
              <form onSubmit={changeEmail} className="p-3 bg-[#1B132B] rounded space-y-3 border border-white/5">
                <Input
                  type="email" placeholder="Nouvelle adresse" required
                  value={emailForm.new_email}
                  onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                  className="bg-[#261B3B] border-white/10 text-white"
                  data-testid="change-email-new"
                />
                <PasswordInput
                  value={emailForm.password}
                  onChange={(v) => setEmailForm({ ...emailForm, password: v })}
                  autoComplete="current-password"
                  placeholder="Confirme avec ton mot de passe"
                  testid="change-email-password"
                />
                <Button type="submit" disabled={emailLoading} className="gradient-primary" data-testid="change-email-submit">
                  {emailLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Envoyer la confirmation
                </Button>
              </form>
            )}

            <div className="flex items-center justify-between p-3 bg-[#1B132B] rounded">
              <div>
                <p className="text-sm text-white">Mot de passe</p>
                <p className="text-xs text-[#B59ED1]">Modifié il y a quelques temps</p>
              </div>
              <Button size="sm" variant="outline" className="border-white/10 text-[#00E5FF]" onClick={() => setShowPwd(!showPwd)} data-testid="security-change-password-toggle">
                Changer
              </Button>
            </div>
            {showPwd && (
              <form onSubmit={changePassword} className="p-3 bg-[#1B132B] rounded space-y-3 border border-white/5">
                <PasswordInput
                  value={pwdForm.old}
                  onChange={(v) => setPwdForm({ ...pwdForm, old: v })}
                  autoComplete="current-password"
                  placeholder="Mot de passe actuel"
                  testid="change-password-old"
                />
                <PasswordInput
                  value={pwdForm.new}
                  onChange={(v) => setPwdForm({ ...pwdForm, new: v })}
                  autoComplete="new-password"
                  placeholder="Nouveau mot de passe"
                  testid="change-password-new"
                />
                <Button type="submit" disabled={pwdLoading} className="gradient-primary" data-testid="change-password-submit">
                  {pwdLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Mettre à jour
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {user.auth_method === 'google' && (
        <Card className="bg-[#261B3B] border-white/10" data-testid="set-initial-password-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#00E5FF]" />
              Définir un mot de passe
            </CardTitle>
            <p className="text-xs text-[#B59ED1] mt-1">
              Ton compte est actuellement lié à Google. En définissant un mot de passe, tu pourras aussi te connecter par email — utile si Google est indisponible.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!showSetInitial ? (
              <Button variant="outline" size="sm" className="border-[#00E5FF]/40 text-[#00E5FF]" onClick={() => setShowSetInitial(true)} data-testid="set-initial-password-toggle">
                <Lock className="w-4 h-4 mr-2" />
                Définir mon mot de passe
              </Button>
            ) : (
              <form onSubmit={setInitialPassword} className="space-y-3 p-3 bg-[#1B132B] rounded border border-white/5">
                <PasswordInput
                  value={initialPwdForm.new}
                  onChange={(v) => setInitialPwdForm({ new: v })}
                  autoComplete="new-password"
                  placeholder="Nouveau mot de passe (min. 8 caractères)"
                  testid="set-initial-password-new"
                />
                <div className="flex gap-2">
                  <Button type="submit" disabled={initialPwdLoading} className="gradient-primary" data-testid="set-initial-password-submit">
                    {initialPwdLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Confirmer
                  </Button>
                  <Button type="button" variant="ghost" className="text-[#B59ED1]" onClick={() => { setShowSetInitial(false); setInitialPwdForm({ new: '' }); }}>
                    Annuler
                  </Button>
                </div>
                <p className="text-[10px] text-[#B59ED1] leading-relaxed">
                  Une fois validé, ton compte basculera en mode email/mot de passe. Tu pourras toujours te reconnecter via Google si besoin.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Zone dangereuse */}
      <Card className="bg-[#261B3B] border-red-500/30">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-red-400 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Zone dangereuse
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#B59ED1]">
            Désactiver ton compte t'empêchera de te connecter et masquera tes informations.
            Tes commandes et rendez-vous sont conservés (obligations comptables).
          </p>
          {!showDelete ? (
            <Button onClick={() => setShowDelete(true)} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="delete-account-toggle">
              Supprimer mon compte
            </Button>
          ) : (
            <form onSubmit={deleteAccount} className="space-y-3 p-3 bg-red-500/5 rounded border border-red-500/20">
              {isEmail && (
                <PasswordInput
                  value={delForm.password}
                  onChange={(v) => setDelForm({ ...delForm, password: v })}
                  placeholder="Ton mot de passe"
                  autoComplete="current-password"
                  testid="delete-password"
                />
              )}
              <Input
                value={delForm.confirmation}
                onChange={(e) => setDelForm({ ...delForm, confirmation: e.target.value })}
                placeholder='Tape "SUPPRIMER" pour confirmer'
                className="bg-[#1B132B] border-red-500/40 text-white"
                data-testid="delete-confirmation"
              />
              <div className="flex gap-2">
                <Button type="submit" disabled={delLoading} className="bg-red-500 hover:bg-red-600 text-white" data-testid="delete-account-confirm">
                  {delLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Confirmer la suppression
                </Button>
                <Button type="button" onClick={() => { setShowDelete(false); setDelForm({ password: '', confirmation: '' }); }} variant="ghost" className="text-[#B59ED1]">
                  Annuler
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
